import React from 'react';
import ArticleLayout from './ArticleLayout';
import Footer from '../../../../components/Footer/Footer';
import { AssetUrls } from 'config/assets';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';

interface ShippingItem {
  label: string;
  img: string;
  imgStyle: React.CSSProperties;
  content: React.ReactNode;
  imageLink?: string;
}

interface ShippingSection {
  title: string;
  description: string;
  items: ShippingItem[];
}

const SECTIONS: ShippingSection[] = [
  {
    title: 'Small Packages (1–3 Cards)',
    description: 'Supplies you’ll need:',
    items: [
      {
        label: 'Envelope (A1 Size)',
        img: '1b-A1Envelope',
        imgStyle: {
          width: '420px',
          height: 'auto',
          transform: 'translateX(-60%)',
        },
        imageLink: 'https://amzn.to/3JIjCbp',
        content: (
          <>
            <p>
              A1's are perfectly sized for toploaders. Larger envelopes can snag or tear in sorting machines.
            </p>
            <p>Use a Forever Stamp if shipping domestically within the USA, or a Global Forever Stamp if shipping internationally.</p>
          </>
        ),
      },
      {
        label: 'Soft Sleeve',
        img: '2b-SoftSleeve',
        imgStyle: {
          width: '220px',
          height: 'auto',
          transform: 'translateX(-25%)',
        },
        imageLink: 'https://amzn.to/3I3Fwp4',
        content: (
          <>
            <p>Protects the surface of the card.</p>
            <p>
              <strong>💡 Pro Tip:</strong><br />
              Position the open edge of the sleeve deep inside the most protected part of the toploader.
              <li>Place the card upside down in the soft sleeve.</li>
              <li>Then insert the sleeve right side up into the toploader.</li>
              
            </p>
          </>
        ),
      },
      {
        label: 'Hard Plastic Toploader',
        img: '3-Toploader',
        imgStyle: {
          width: '220px',
          height: 'auto',
          transform: 'translateX(-25%)',
        },
        imageLink: 'https://amzn.to/47Z24ln',
        content: <p>Prevents bending. Holds up to 3 sleeved cards comfortably.</p>,
      },
      {
        label: 'Post-it note',
        img: '4b-Postitnote',
        imgStyle: {
          width: '80px',
          height: 'auto',
          transform: 'translateX(90%)',
        },
        imageLink: 'https://amzn.to/4lVaLRe',
        content: <p>Fold it over the open edge of the toploader to prevent cards from sliding out. These are easy to remove, unlike tape.</p>,
      },
      {
        label: 'Best Practices',
        img: '5b-a1package',
        imgStyle: {
          width: '420px',
          height: 'auto',
          transform: 'translateX(-60%)',
        },
        content: <p>If the card is worth $50 or more, use tracking and a bubble mailer (see below).</p>,
      },
    ],
  },

  {
    title: 'Medium Packages (4–10 Cards)',
    description: 'This is the upper limit of what you can fit in a standard A1 envelope with a single Forever stamp. A toploader, team bag, and 10 cards are just barely under the USPS weight limit for First-Class postage. Use everything above, plus:',
    items: [
      {
        label: 'Team Bag',
        img: '6-TeamBag',
        imgStyle: {
          width: '220px',
          height: 'auto',
          transform: 'translateX(-28%)',
        },
        imageLink: 'https://amzn.to/45ZS2hl',
        content: <p>A resealable baggie used to package multiple cards tightly inside.</p>,
      },
      {
        label: '10-Card Stack',
        img: '7-tencards',
        imgStyle: {
          width: '420px',
          height: 'auto',
          transform: 'translateX(-62%)',
        },
        content: <p>This method can support 10 cards with a domestic forever stamp.</p>,
      },
      {
        label: 'Packing Strategy',
        img: '8-teambagGroup',
        imgStyle: {
          width: '180px',
          height: 'auto',
          transform: 'translateX(-12%)',
        },
        content: (
          <ul>
            <li>Bundle 3 card in toploader (same as above)</li>
            <li>Put the toploader into the team bag</li>
            <li>Put the remaining cards into the team bag and seal it</li>
            <li>Put everything into the A1 envelope</li>
            <li>Still ships with 1 Forever Stamp</li>
          </ul>
        ),
      },
    ],
  },

  {
    title: 'Large Packages (10+ Cards)',
    description: 'Once you’re over 10 cards or the package is worth more than $50, you’ll  need a tracking number. It’s time to upgrade your supplies. Here’s what you’ll need:',
    items: [
      {
        label: '4x7 Padded Bubble Mailer',
        img: '9-paddedEnvelope',
        imgStyle: {
          width: '380px',
          height: 'auto',
          transform: 'translateX(-61%)',
        },
        imageLink: 'https://amzn.to/3I1g5V5',
        content: <p>Adds cushioning and protects from crushing during transit.</p>,
      },
      {
        label: 'Hard Plastic Hinged Box',
        img: '10-hingedbox',
        imgStyle: {
          width: '280px',
          height: 'auto',
          transform: 'translateX(-50%)',
        },
        imageLink: 'https://amzn.to/4naVKf6',
        content: (
          <p>
            These come in different sizes and can protect large quantities of cards.<br />
            Prevents shifting and corner dings and protects against significant crush force.
          </p>
        ),
      },
      {
        label: 'Packing Peanuts',
        img: '11-packingpeanuts',
          imgStyle: {
          width: '80px',
          height: 'auto',
          transform: 'translateX(80%)',
        },
        content: (
          <p>
            Save these from other deliveries—they make great spacers. Bubble wrap is great to re-use as well.
          </p>
        ),
      },
    ],
  },
];

const ShippingGuide = () => {
  return (
    <>
      <SEOHelmet
        title="How to Ship Magic Cards Safely - TradeMagic Shipping Guide"
        description="Professional guide to shipping Magic: The Gathering cards safely. Learn proper packaging with toploaders, team bags, and bubble mailers."
        canonical="https://trademagic.gg/articles/shipping"
        keywords="ship MTG cards, Magic card shipping, toploader shipping, how to mail trading cards"
        type="article"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg' },
          { name: 'Articles', url: 'https://trademagic.gg/articles' },
          { name: 'Shipping Guide', url: 'https://trademagic.gg/articles/shipping' },
        ]}
      />

      <ArticleLayout
        title="Shipping Guide"
        hero={AssetUrls.articles.shippingGuide}
      >
      <div className="shipping-intro">
        <h2>
          How to Ship Magic Cards
          <br />
          Safely and Professionally
        </h2>
        <p>
          Whether you’re trading, selling, or gifting Magic cards, shipping them correctly is critical.
          Improper packaging can lead to lost cards, bent corners, or angry recipients. This guide walks
          you through the right way to ship anywhere from a single card to bulk lots—protecting your cards,
          your reputation, and your peace of mind.
        </p>
      </div>

      {SECTIONS.map(({ title, description, items }) => (
        <section className="shipping-section" key={title}>
                <h2>{title}</h2>
                <p>{description}</p>
          {items.map(({ img, label, imgStyle, content, imageLink }) => (
            <div className="supply-row" key={label}>
              <div className="image-wrapper">
                {imageLink ? (
                  <a href={imageLink} target="_blank" rel="noopener noreferrer">
                    <img
                      src={AssetUrls.articles.byName(img)}
                      alt={label}
                      style={{...imgStyle, cursor: 'pointer'}}
                    />
                  </a>
                ) : (
                  <img
                    src={AssetUrls.articles.byName(img)}
                    alt={label}
                    style={imgStyle}
                  />
                )}
              </div>
              <div className="supply-description">
                <h4>{label}</h4>
                {content}
              </div>
            </div>
          ))}
        </section>
      ))}

<div className="shipping-outro">
  <h2>Final Tips</h2>
  <p>
    <li>Label clearly with both return and destination addresses.</li>
    <li>Weigh your package if you're unsure—underpayment can delay or return your shipment.</li>
    <li>Reuse supplies when possible, but don’t skimp on protection.</li>
    <li>Always include your packing slip (you can print this when you initiate a trade).</li>
  </p>
  <p>
    Proper shipping isn't just a nice-to-have—it's essential. Protect your cards and your reputation by investing in a few simple supplies and following the steps in this guide. The collectors you trade with will thank you (and trade with you again).
  </p>
</div>

      <Footer />
    </ArticleLayout>
    </>
  );
};

export default ShippingGuide;
